import { LockKey } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { usePublicShare } from "~/api/queries";
import { Empty } from "~/foundation/components/Empty";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";
import { useDocumentTitle } from "~/foundation/hooks/useDocumentTitle";
import { formatDateTime } from "~/foundation/utils/time";

/**
 * The one authenticated-shell-free read surface. It deliberately renders outside
 * AuthenticatedLayout: a share link must work for someone with no session at all.
 */
export function SharedDocumentScene({ shareId }: { shareId: string }) {
  const { t, i18n } = useTranslation();
  const { data, isLoading, isError } = usePublicShare(shareId);
  useDocumentTitle(data?.document.title ?? t("Shared document"));

  if (isLoading) {
    return (
      <div className="flex h-dvh items-center justify-center">
        <Spinner label={t("Loading")} />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex h-dvh items-center justify-center">
        <Empty
          icon={LockKey}
          title={t("This link is no longer available")}
          message={t("It may have been revoked, or it never existed.")}
        />
      </div>
    );
  }

  const { document } = data;

  return (
    <div className="min-h-dvh bg-bg">
      <header className="flex h-[var(--console-bar-height)] items-center justify-between border-b border-divider px-4">
        <Link to="/" className="text-body font-medium text-text">
          Slate
        </Link>
        <Text variant="small">{t("Shared publicly")}</Text>
      </header>

      <main className="mx-auto flex w-full max-w-3xl flex-col gap-4 p-6">
        <div className="flex items-start gap-3">
          <span aria-hidden className="text-h1 leading-none">
            {document.emoji ?? "📄"}
          </span>
          <div className="min-w-0">
            <Text as="h1" variant="h1">
              {document.title}
            </Text>
            <Text variant="small" className="mt-1">
              {t("Updated {{when}}", {
                when: formatDateTime(document.updatedAt, i18n.language),
              })}
            </Text>
          </div>
        </div>

        <div className="rounded-md border border-divider p-4">
          <Text variant="secondary">
            {document.summary || t("This document has no content yet.")}
          </Text>
        </div>
      </main>
    </div>
  );
}
